import { useState, useEffect } from 'react';
import { X, Settings, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useCookieConsent, CookieConsentType } from '@/hooks/useCookieConsent';

export const CookieBanner = () => {
  const { consent, bannerVisible, acceptAll, rejectAll, updateConsent } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [tempConsent, setTempConsent] = useState<CookieConsentType>(consent);

  // Listen for cookie settings event from footer
  useEffect(() => {
    const handleOpenCookieSettings = () => {
      setTempConsent(consent);
      setShowSettings(true);
    };

    window.addEventListener('openCookieSettings', handleOpenCookieSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenCookieSettings);
  }, [consent]);

  if (!bannerVisible) return null;

  const handleSaveSettings = () => {
    updateConsent(tempConsent);
    setShowSettings(false);
  };

  const handleOpenSettings = () => {
    setTempConsent(consent);
    setShowSettings(true);
  };

  return (
    <>
      {/* Main Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-border shadow-lg animate-in slide-in-from-bottom-5">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <Cookie className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div className="flex-1">
                <p className="text-sm text-foreground font-medium mb-1">
                  Nous utilisons des cookies
                </p>
                <p className="text-xs text-muted-foreground">
                  Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic. 
                  En cliquant sur "Accepter", vous consentez à l'utilisation de tous les cookies.{" "}
                  <a href="/politique-confidentialite" className="text-primary hover:underline">
                    Politique de confidentialité
                  </a>
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenSettings}
                className="text-xs"
              >
                <Settings className="h-3 w-3 mr-1" />
                Paramètres
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={rejectAll}
                className="text-xs"
              >
                Refuser
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="text-xs"
              >
                Accepter tout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="h-5 w-5" />
              Paramètres des cookies
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="space-y-4">
              {/* Essential Cookies */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">
                    Cookies essentiels
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Nécessaires au fonctionnement du site (authentification, préférences). 
                    Conservés pendant la session. Ne peuvent pas être désactivés.
                  </p>
                </div>
                <Switch
                  checked={true}
                  disabled={true}
                  aria-label="Cookies essentiels (toujours activés)"
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">
                    Cookies analytiques
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Google Analytics (_ga, _gid) pour analyser l'utilisation du site. 
                    Conservés 2 ans. <a href="/politique-confidentialite" className="text-primary hover:underline">En savoir plus</a>
                  </p>
                </div>
                <Switch
                  checked={tempConsent.analytics}
                  onCheckedChange={(checked) =>
                    setTempConsent(prev => ({ ...prev, analytics: checked }))
                  }
                  aria-label="Cookies analytiques"
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">
                    Cookies marketing
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Cookies publicitaires tiers pour le ciblage et la mesure. 
                    Conservés jusqu'à 2 ans selon les fournisseurs.
                  </p>
                </div>
                <Switch
                  checked={tempConsent.marketing}
                  onCheckedChange={(checked) =>
                    setTempConsent(prev => ({ ...prev, marketing: checked }))
                  }
                  aria-label="Cookies marketing"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowSettings(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSaveSettings}
                className="flex-1"
              >
                Enregistrer les préférences
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};